import { humanize } from 'inflected';
import { ReactNode, useMemo, useState } from 'react';
import { Nav } from 'react-bootstrap';
import { MdStar } from 'react-icons/md';
import { AMAZON, Platform } from './CommerceScraperUtils';

// ── Types ─────────────────────────────────────────────────────────────

interface SubTabDef {
  key: string;
  label: string;
  hidden?: boolean;
}

// ── Helpers ───────────────────────────────────────────────────────────

const str = (val: unknown): string => (val == null ? '' : String(val));
const bool = (val: unknown): boolean => Boolean(val);

const stripBrandPrefix = (brand: unknown): string => {
  const s = str(brand);
  return s.replace(/^Brand:\s*/i, '');
};

const joinPath = (val: unknown): string => {
  if (Array.isArray(val)) {
    return val.join(' > ');
  }
  return str(val);
};

const nameOrString = (val: unknown): string => {
  if (val && typeof val === 'object' && 'name' in (val as Record<string, unknown>)) {
    return str((val as Record<string, unknown>).name);
  }
  return str(val);
};

// ── KV Table ──────────────────────────────────────────────────────────

interface KVEntry {
  label: string;
  value: string;
}

const KVTable = ({ entries }: { entries: KVEntry[] }) => {
  const filtered = useMemo(() => entries.filter(e => e.value !== ''), [entries]);
  if (!filtered.length) {
    return <p className='scraper-muted'>No data</p>;
  }
  return (
    <table className='scraper-kv-simple'>
      <tbody>
        {filtered.map(({ label, value }) => (
          <tr key={label}>
            <td className='kv-label'>{label}</td>
            <td className='kv-val'>{value}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
};

// ── Rating Stars ──────────────────────────────────────────────────────

const Stars = ({ rating }: { rating: number }) => (
  <span className='star-display'>
    {[1, 2, 3, 4, 5].map(i => (
      <MdStar key={i} className={i <= Math.round(rating) ? 'star-filled' : 'star-empty'} />
    ))}
  </span>
);

// ── Review Card ───────────────────────────────────────────────────────

const ReviewCard = ({ review }: { review: Record<string, unknown> }) => {
  const rating = Number(review.stars ?? review.rating ?? 0);
  const author = str(review.username || review.reviewer_name || review.author);
  const body = str(review.review || review.body || review.text);
  const rawTitle = str(review.title || 'No title');
  const title = rawTitle.replace(/^[\d.]+ out of \d+ stars[\s\S]*?\n\s+/, '').trim();
  const date = str(review.date || review.date_published);
  const verified =
    bool(review.verified_purchase) ||
    (Array.isArray(review.badges) && (review.badges as string[]).includes('Verified Purchase'));
  const helpful = str(review.total_found_helpful || review.helpful_votes);

  return (
    <div className='review-card'>
      <div className='review-header'>
        <span className='review-title'>{title}</span>
        <Stars rating={rating} />
      </div>
      <div className='review-meta'>
        {author && <span>{author}</span>}
        {date && <span>{date}</span>}
        {verified && <span className='meta-badge badge-avail'>Verified</span>}
        {helpful && <span>{helpful} found helpful</span>}
      </div>
      {body && <p className='review-body'>{body}</p>}
    </div>
  );
};

// ── Rating Bars ───────────────────────────────────────────────────────

const RatingBars = ({ product }: { product: Record<string, unknown> }) => {
  const bars = [5, 4, 3, 2, 1].map(star => ({
    star,
    pct: Number(product[`${star}_star_percentage`] ?? 0)
  }));
  if (bars.every(b => b.pct === 0)) {
    return null;
  }
  return (
    <div className='rating-bars'>
      {bars.map(({ star, pct }) => (
        <div key={star} className='rating-bar-row'>
          <span className='rating-bar-label'>{star} star</span>
          <div className='rating-bar-track'>
            <div className='rating-bar-fill' style={{ width: `${pct}%` }} />
          </div>
          <span className='rating-bar-pct'>{pct}%</span>
        </div>
      ))}
    </div>
  );
};

// ── Rankings Table ────────────────────────────────────────────────────

const RankingsTable = ({ product }: { product: Record<string, unknown> }) => {
  const pi = product.product_information as Record<string, unknown> | undefined;
  const rawBsr =
    pi?.best_sellers_rank ??
    pi?.bestsellers_rank ??
    product.best_sellers_rank ??
    product.bestsellers_rank;

  if (!Array.isArray(rawBsr) || !rawBsr.length) {
    return <p className='scraper-muted'>No ranking data available.</p>;
  }

  const entries: KVEntry[] = (rawBsr as unknown[]).flatMap(entry => {
    if (typeof entry === 'string') {
      const clean = entry.replace(/\s*\(See\b[^)]*\)\s*$/i, '').trim();
      const m = clean.match(/^#([\d,]+)\s+in\s+(.+)$/);
      if (m) return [{ label: m[1], value: m[2].trim() }];
      return clean ? [{ label: '', value: clean }] : [];
    }
    if (entry && typeof entry === 'object') {
      const e = entry as Record<string, unknown>;
      return [
        {
          label: str(e.rank ?? e.position ?? ''),
          value: str(e.category ?? e.name ?? e.label ?? '')
        }
      ];
    }
    return [];
  });

  return <KVTable entries={entries} />;
};

// ── Variants Table ────────────────────────────────────────────────────

const VariantsTable = ({ product }: { product: Record<string, unknown> }) => {
  const byType = useMemo(() => {
    const groups: Record<string, { asin: string; value: string; isCurrent: boolean }[]> = {};
    const customOpts = product.customization_options as
      Record<string, unknown[]> | null | undefined;
    if (customOpts && typeof customOpts === 'object' && !Array.isArray(customOpts)) {
      for (const [dim, entries] of Object.entries(customOpts)) {
        if (!Array.isArray(entries)) continue;
        groups[dim] = (entries as Record<string, unknown>[]).map(e => ({
          asin: str(e.asin ?? ''),
          value: str(e.value ?? e.asin ?? ''),
          isCurrent: Boolean(e.is_selected)
        }));
      }
    }
    return groups;
  }, [product.customization_options]);

  if (!Object.keys(byType).length) {
    return <p className='scraper-muted'>No variant data available.</p>;
  }

  return (
    <div>
      {Object.entries(byType).map(([dim, items]) => (
        <div key={dim} className='variant-group'>
          <div className='variant-group-label'>{dim}</div>
          <div className='variant-chips'>
            {items.map((item, i) => (
              <span
                key={i}
                className={`variant-chip${item.isCurrent ? ' variant-chip-current' : ''}`}
                title={item.asin}
              >
                {item.value}
              </span>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

// ── JSON Viewer with syntax highlighting ──────────────────────────────

const TOKEN_RE =
  /("(\\u[\da-fA-F]{4}|\\[^u]|[^\\"])*"(\s*:)?|\b(true|false|null)\b|-?\d+(?:\.\d*)?(?:[eE][+-]?\d+)?)/g;

const tokenClass = (token: string) => {
  if (/^"/.test(token)) return /:$/.test(token) ? 'json-key' : 'json-string';
  if (/true|false/.test(token)) return 'json-bool';
  if (/null/.test(token)) return 'json-null';
  return 'json-number';
};

const HighlightedJson = ({ data }: { data: unknown }) => {
  const elements = useMemo(() => {
    const raw = JSON.stringify(data, null, 2);
    const parts: ReactNode[] = [];
    let lastIndex = 0;

    for (const m of raw.matchAll(TOKEN_RE)) {
      const idx = m.index;
      if (idx > lastIndex) {
        parts.push(raw.slice(lastIndex, idx));
      }
      parts.push(
        <span key={idx} className={tokenClass(m[0])}>
          {m[0]}
        </span>
      );
      lastIndex = idx + m[0].length;
    }
    if (lastIndex < raw.length) {
      parts.push(raw.slice(lastIndex));
    }
    return parts;
  }, [data]);

  return <pre className='json-viewer'>{elements}</pre>;
};

// ── Sub-Tabs ──────────────────────────────────────────────────────────

interface SubTabsProps {
  tabs: SubTabDef[];
  activeTab: string;
  onTabChange: (key: string) => void;
}

const SubTabs = ({ tabs, activeTab, onTabChange }: SubTabsProps) => (
  <Nav bsPrefix='result-tabs' activeKey={activeTab} onSelect={key => key && onTabChange(key)}>
    {tabs
      .filter(t => !t.hidden)
      .map(t => (
        <Nav.Item key={t.key}>
          <Nav.Link bsPrefix='result-tab' eventKey={t.key}>
            {t.label}
          </Nav.Link>
        </Nav.Item>
      ))}
  </Nav>
);

// ── Amazon Product Preview ────────────────────────────────────────────

const AmazonProductPreview = ({ product }: { product: Record<string, unknown> }) => {
  const [subTab, setSubTab] = useState('summary');
  const p = product;

  const images = (p.high_res_images || p.images) as string[] | undefined;
  const image = images?.[0];
  const name = str(p.name);
  const price = str(p.pricing);
  const listPrice = str(p.list_price);
  const shipping = str(p.shipping_price);
  const rating = Number(p.average_rating ?? 0);
  const totalReviews = str(p.total_reviews);
  const brand =
    stripBrandPrefix(p.brand) ||
    str((p.product_information as Record<string, unknown> | undefined)?.manufacturer ?? '');
  const availability = str(p.availability_status);
  const fba = bool(p.fulfilled_by_amazon);
  const aplus = bool(p.aplus_present);
  const coupon = bool(p.is_coupon_exists);
  const bullets = (p.feature_bullets as string[]) || [];
  const reviews = (p.reviews as Record<string, unknown>[]) || [];
  const productInfo = p.product_information as Record<string, unknown> | undefined;
  const description = str(p.full_description);
  const asin = str(p.asin);
  const prime = p.is_prime ?? p.prime;
  const amazonsChoice = p.is_amazons_choice;
  const dealPrice = str(p.deal_price);
  const newerModel = p.newer_model as Record<string, unknown> | undefined;
  const newerVersionAsin = str(newerModel?.asin ?? p.newer_version_asin);
  const newerVersionTitle = str(newerModel?.title ?? p.newer_version_title);

  const pi = productInfo;
  const rawBsr =
    pi?.best_sellers_rank ?? pi?.bestsellers_rank ?? p.best_sellers_rank ?? p.bestsellers_rank;
  const hasRankings = Array.isArray(rawBsr) && rawBsr.length > 0;
  const isTopRankedBsr =
    Array.isArray(rawBsr) &&
    rawBsr.some(r => {
      if (typeof r === 'string') return r.match(/^#([\d,]+)\b/)?.[1].replace(/,/g, '') === '1';
      if (r && typeof r === 'object') {
        const e = r as Record<string, unknown>;
        return String(e.rank ?? e.position ?? '').trim() === '1';
      }
      return false;
    });
  const bestSeller = p.is_best_seller ?? isTopRankedBsr;
  const hasVariants =
    (p.customization_options != null &&
      typeof p.customization_options === 'object' &&
      Object.keys(p.customization_options).length > 0) ||
    (Array.isArray(p.product_variants) && (p.product_variants as unknown[]).length > 0) ||
    (Array.isArray(p.variants) && (p.variants as unknown[]).length > 0);

  const yesNo = (v: unknown): string => {
    if (v == null) return '';
    return bool(v) ? 'Yes' : 'No';
  };

  const summaryEntries: KVEntry[] = useMemo(
    () => [
      { label: 'ASIN', value: asin },
      { label: 'Parent ASIN', value: str(p.parent_asin) },
      { label: 'Brand', value: brand },
      { label: 'Brand URL', value: str(p.brand_url) },
      { label: 'Price', value: price },
      { label: 'List Price', value: listPrice },
      { label: 'Deal Price', value: dealPrice },
      { label: 'Deal Type', value: str(p.deal_type) },
      { label: 'S&S Price', value: str(p.sns_price ?? p.subscribe_price) },
      { label: 'One-time Price', value: str(p.one_time_price) },
      { label: 'Coupon', value: coupon ? 'Yes' : '' },
      { label: 'Coupon Discount', value: str(p.coupon_discount_percentage ?? p.coupon_percentage) },
      { label: 'Coupon Details', value: str(p.coupon_details) },
      { label: 'Shipping', value: shipping },
      { label: 'Est. Delivery', value: str(p.shipping_time) },
      { label: 'Fast Track', value: str(p.fast_track_message) },
      { label: 'Availability', value: availability },
      { label: 'Subscribe & Save', value: yesNo(p.is_subscribe_and_save ?? p.subscribe_and_save) },
      { label: 'Fulfilled by Amazon', value: fba ? 'Yes' : 'No' },
      { label: 'Sold By', value: nameOrString(p.sold_by) },
      { label: 'Sold By Link', value: str(p.sold_by_link) },
      { label: 'Ships From', value: nameOrString(p.ships_from) },
      { label: 'Other Sellers', value: str(p.other_sellers_count ?? p.num_other_sellers) },
      { label: 'Seller Name', value: str(p.seller_name) },
      { label: 'Seller ID', value: str(p.seller_id) },
      { label: 'Rating', value: rating ? String(rating) : '' },
      { label: 'Total Reviews', value: totalReviews },
      { label: 'Total Ratings', value: str(p.total_ratings) },
      { label: 'A+ Content', value: aplus ? 'Yes' : 'No' },
      { label: 'Prime', value: yesNo(prime) },
      { label: "Amazon's Choice", value: yesNo(amazonsChoice) },
      { label: 'Best Seller', value: yesNo(bestSeller) },
      { label: 'Climate Pledge', value: yesNo(p.climate_pledge_friendly) },
      { label: 'Business Only', value: str(p.is_business_only ?? p.business_only) },
      { label: 'Video', value: yesNo(p.has_video ?? p.video) },
      { label: 'Category', value: joinPath(p.product_category) },
      { label: 'Newer Version ASIN', value: newerVersionAsin },
      { label: 'Newer Version Title', value: newerVersionTitle },
      { label: 'Product URL', value: str(p.product_url ?? p.url) },
      { label: 'Currency', value: str(p.currency) }
    ],
    [
      p,
      asin,
      brand,
      price,
      listPrice,
      shipping,
      availability,
      fba,
      aplus,
      coupon,
      rating,
      totalReviews,
      prime,
      amazonsChoice,
      bestSeller,
      dealPrice,
      newerVersionAsin,
      newerVersionTitle
    ]
  );

  const productInfoEntries: KVEntry[] = useMemo(() => {
    if (!productInfo || typeof productInfo !== 'object') {
      return [];
    }
    return Object.entries(productInfo).map(([k, v]) => ({ label: k, value: str(v) }));
  }, [productInfo]);

  const tabs: SubTabDef[] = [
    { key: 'summary', label: 'Summary' },
    { key: 'rankings', label: 'Rankings', hidden: !hasRankings },
    { key: 'variants', label: 'Variants', hidden: !hasVariants },
    { key: 'info', label: 'Product Info', hidden: !productInfoEntries.length },
    { key: 'desc', label: 'Description', hidden: !description },
    { key: 'reviews', label: `Reviews (${reviews.length})`, hidden: !reviews.length },
    { key: 'json', label: 'Raw JSON' }
  ];

  return (
    <div className='product-preview' data-testid='product-preview'>
      <div className='product-card'>
        <div className='product-image-wrap'>
          {image ? (
            <img className='product-image' src={image} alt={name} />
          ) : (
            <div className='product-image-placeholder'>No Image</div>
          )}
        </div>
        <div className='product-info'>
          <div className='product-name'>{name}</div>
          <div className='product-meta'>
            {price && (
              <span className='meta-badge badge-price'>
                {price}
                {listPrice && listPrice !== price && <s className='list-price'>{listPrice}</s>}
              </span>
            )}
            {dealPrice && <span className='meta-badge badge-price'>Deal: {dealPrice}</span>}
            {shipping && <span className='meta-badge badge-avail'>+ {shipping} shipping</span>}
            {rating > 0 && (
              <span className='meta-badge badge-rating'>
                <MdStar /> {rating} ({totalReviews})
              </span>
            )}
            {brand && <span className='meta-badge badge-brand'>Brand: {brand}</span>}
            {availability && <span className='meta-badge badge-avail'>{availability}</span>}
            {fba && <span className='meta-badge badge-avail'>FBA</span>}
            {bool(prime) && <span className='meta-badge badge-prime'>Prime</span>}
            {bool(amazonsChoice) && (
              <span className='meta-badge badge-choice'>Amazon&apos;s Choice</span>
            )}
            {bool(bestSeller) && <span className='meta-badge badge-best'>Best Seller</span>}
            {bool(p.climate_pledge_friendly) && (
              <span className='meta-badge badge-avail'>Climate Pledge</span>
            )}
            {aplus && <span className='meta-badge badge-brand'>A+</span>}
            {coupon && <span className='meta-badge badge-price'>Coupon</span>}
            {newerVersionAsin && (
              <span className='meta-badge badge-rating'>Newer Version Available</span>
            )}
          </div>
          {bullets.length > 0 && (
            <ul className='product-bullets'>
              {bullets.map((b, i) => (
                <li key={i}>{b}</li>
              ))}
            </ul>
          )}
        </div>
      </div>

      <SubTabs tabs={tabs} activeTab={subTab} onTabChange={setSubTab} />

      <div className='subtab-content'>
        {subTab === 'summary' && (
          <>
            <KVTable entries={summaryEntries} />
            <RatingBars product={p} />
          </>
        )}
        {subTab === 'rankings' && <RankingsTable product={p} />}
        {subTab === 'variants' && <VariantsTable product={p} />}
        {subTab === 'info' && <KVTable entries={productInfoEntries} />}
        {subTab === 'desc' && <p className='product-description'>{description}</p>}
        {subTab === 'reviews' && (
          <div className='review-list'>
            {reviews.map((r, i) => (
              <ReviewCard key={i} review={r} />
            ))}
          </div>
        )}
        {subTab === 'json' && <HighlightedJson data={p} />}
      </div>
    </div>
  );
};

// ── Walmart Product Preview ───────────────────────────────────────────

const WalmartProductPreview = ({ product }: { product: Record<string, unknown> }) => {
  const [subTab, setSubTab] = useState('summary');
  const p = product;

  const images = p.images as string[] | undefined;
  const image = images?.[0];
  const name = str(p.product_name || p.name);
  const price = p.price_currency ? `${p.price_currency}${p.price}` : str(p.price);
  const rating = Number(p.rating ?? p.average_rating ?? 0);
  const totalReviews = str(p.total_reviews);
  const brand = str(p.brand);
  const availability = humanize(str(p.product_availability));
  const shortDesc = str(p.product_short_description);
  const longDesc = str(p.product_long_description);
  const description = shortDesc || longDesc;
  const reviews = (p.reviews as Record<string, unknown>[]) || [];
  const productId = str(p.product_id || p.us_item_id);

  const summaryEntries: KVEntry[] = useMemo(
    () => [
      { label: 'Product ID', value: productId },
      { label: 'SKU', value: str(p.sku) },
      { label: 'Brand', value: brand },
      { label: 'Category', value: str(p.product_category) },
      { label: 'Price', value: price },
      { label: 'Availability', value: availability },
      { label: 'Sold By', value: str(p.sold_by) },
      { label: 'Seller Type', value: str(p.seller_type) },
      { label: 'Rating', value: rating ? String(rating) : '' },
      { label: 'Total Reviews', value: totalReviews },
      { label: 'Product URL', value: str(p.product_url) }
    ],
    [p, productId, brand, price, availability, rating, totalReviews]
  );

  const specEntries: KVEntry[] = useMemo(() => {
    const specs = (p.specifications as { name: string; value: string }[]) || [];
    return specs.map(s => ({ label: s.name, value: s.value }));
  }, [p.specifications]);

  const tabs: SubTabDef[] = [
    { key: 'summary', label: 'Summary' },
    { key: 'specs', label: 'Specifications', hidden: !specEntries.length },
    { key: 'desc', label: 'Description', hidden: !description },
    { key: 'reviews', label: `Reviews (${reviews.length})`, hidden: !reviews.length },
    { key: 'json', label: 'Raw JSON' }
  ];

  return (
    <div className='product-preview' data-testid='product-preview'>
      <div className='product-card'>
        <div className='product-image-wrap'>
          {image ? (
            <img className='product-image' src={image} alt={name} />
          ) : (
            <div className='product-image-placeholder'>No Image</div>
          )}
        </div>
        <div className='product-info'>
          <div className='product-name'>{name}</div>
          <div className='product-meta'>
            {price && <span className='meta-badge badge-price'>{price}</span>}
            {rating > 0 && (
              <span className='meta-badge badge-rating'>
                <MdStar /> {rating} ({totalReviews})
              </span>
            )}
            {brand && <span className='meta-badge badge-brand'>Brand: {brand}</span>}
            {availability && <span className='meta-badge badge-avail'>{availability}</span>}
          </div>
        </div>
      </div>

      <SubTabs tabs={tabs} activeTab={subTab} onTabChange={setSubTab} />

      <div className='subtab-content'>
        {subTab === 'summary' && <KVTable entries={summaryEntries} />}
        {subTab === 'specs' && <KVTable entries={specEntries} />}
        {subTab === 'desc' && <p className='product-description'>{description}</p>}
        {subTab === 'reviews' && (
          <div className='review-list'>
            {reviews.map((r, i) => (
              <ReviewCard key={i} review={r} />
            ))}
          </div>
        )}
        {subTab === 'json' && <HighlightedJson data={p} />}
      </div>
    </div>
  );
};

// ── Main Export ────────────────────────────────────────────────────────

interface ProductPreviewProps {
  platform: Platform;
  product: Record<string, unknown>;
}

export const ProductPreview = ({ platform, product }: ProductPreviewProps) =>
  platform === AMAZON ? (
    <AmazonProductPreview product={product} />
  ) : (
    <WalmartProductPreview product={product} />
  );

// Re-export for use in ScraperResults
export { HighlightedJson, ReviewCard };
